import { useEffect, useRef } from "react";

/**
 * MouseGlow – performance-optimiert.
 *
 * Vermeidet React-Re-Renders bei jedem `mousemove` (Reflow-Reduktion).
 * DOM-Updates laufen im `requestAnimationFrame`-Zyklus und schreiben
 * ausschließlich in transform/backgroundColor – keine Layout-Trigger.
 */
const MouseGlow = () => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const largeRef = useRef<HTMLDivElement | null>(null);
  const smallRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const target = useRef({ x: 0, y: 0 });
  const visibleRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(pointer: coarse)").matches) return;

    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    wrapper.style.opacity = "0";

    const schedule = () => {
      if (rafRef.current != null) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        const { x, y } = target.current;
        const ratio = Math.min(Math.max(x / window.innerWidth, 0), 1);
        const h = 190 + ratio * (263 - 190);
        const s = 64 + ratio * (70 - 64);
        const l = 51 + ratio * (58 - 51);

        if (largeRef.current) {
          largeRef.current.style.transform = `translate3d(${x - 250}px, ${y - 250}px, 0)`;
          largeRef.current.style.backgroundColor = `hsla(${h}, ${s}%, ${l}%, 0.15)`;
        }
        if (smallRef.current) {
          smallRef.current.style.transform = `translate3d(${x - 100}px, ${y - 100}px, 0)`;
          smallRef.current.style.backgroundColor = `hsla(${h}, ${s}%, ${l}%, 0.1)`;
        }
      });
    };

    const handleMove = (e: MouseEvent) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;
      if (!visibleRef.current) {
        visibleRef.current = true;
        wrapper.style.opacity = "1";
      }
      schedule();
    };

    const handleLeave = () => {
      visibleRef.current = false;
      wrapper.style.opacity = "0";
    };

    window.addEventListener("mousemove", handleMove, { passive: true });
    document.documentElement.addEventListener("mouseleave", handleLeave);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      document.documentElement.removeEventListener("mouseleave", handleLeave);
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="fixed inset-0 pointer-events-none z-50 transition-opacity duration-300"
      style={{ opacity: 0 }}
      aria-hidden="true"
    >
      <div
        ref={largeRef}
        className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full blur-[140px] will-change-transform"
      />
      <div
        ref={smallRef}
        className="absolute top-0 left-0 w-[200px] h-[200px] rounded-full blur-[80px] will-change-transform"
      />
    </div>
  );
};

export default MouseGlow;
